import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import { Alarm, DiagnosticEvent, DiagnosticReport, FirmwareUpdateStatus, Group, LifecycleEvent, MaintDeadlineChangedEvent, MaintInterventionOverdueEvent, PlantStatus, StartupDiagnostics, Variable } from '../types';

const HUB_URL = process.env.REACT_APP_SIGNALR_HUB_URL || 'http://localhost:5288/hubs/plant';

// Heartbeat applicativo (oltre al keep-alive nativo SignalR). Risolve il caso
// "WebSocket aperto ma server-side morto / NAT chiuso silente": il client
// invoca Ping() ogni N ms; se 2 ping consecutivi non rispondono entro PING_TIMEOUT
// → considera la connessione "freezzata" e forza un restart esplicito.
const PING_INTERVAL_MS = 10_000;
const PING_TIMEOUT_MS = 5_000;
const PING_FAILURES_BEFORE_RESET = 2;

// Auto-reconnect builtin SignalR si arrende dopo qualche tentativo. Sopra c'è
// il nostro custom restart loop con backoff infinito (1s → 2s → 5s → 10s → 30s,
// poi 30s costanti) che subentra su `onclose`.
const RESTART_BACKOFF_MS = [1000, 2000, 5000, 10_000, 30_000];

export type ConnectionStatus = 'connecting' | 'connected' | 'reconnecting' | 'disconnected';
export type ConnectionStatusListener = (status: ConnectionStatus, info?: ConnectionStatusInfo) => void;

export interface ConnectionStatusInfo {
  /** Da quando dura lo stato corrente (UTC ms). */
  changedAt: number;
  /** Ultimo successful ping al backend (UTC ms) — usato come "last seen". */
  lastSuccessfulPingAt: number | null;
  /** Numero di tentativi di restart custom in corso (0 = nessuno). */
  restartAttempt: number;
  /** Eventuale messaggio di errore dell'ultimo failure. */
  lastError?: string;
}

export type VariableUpdateCallback = (variable: Variable) => void;
export type AlarmUpdateCallback = (alarm: Alarm) => void;
export type GroupUpdateCallback = (group: Group) => void;
export type PlantStatusUpdateCallback = (plantId: string, status: PlantStatus) => void;
export type StatisticsUpdateCallback = (plantId: number) => void;
export type GatewayDiagnosticsCallback = (gatewayId: string, report: DiagnosticReport) => void;
export type GatewayDiagnosticEventCallback = (gatewayId: string, event: DiagnosticEvent) => void;
export type GatewayLifecycleEventCallback = (gatewayId: string, event: LifecycleEvent) => void;
export type GatewayStartupDiagnosticsCallback = (gatewayId: string, diagnostics: StartupDiagnostics) => void;
export type GatewayFirmwareUpdateCallback = (gatewayId: string, status: FirmwareUpdateStatus) => void;
export type GatewayLogEntriesCallback = (gatewayId: string, entries: any[]) => void;
export type MaintDeadlineChangedCallback = (evt: MaintDeadlineChangedEvent) => void;
export type MaintInterventionOverdueCallback = (evt: MaintInterventionOverdueEvent) => void;

class SignalRService {
  private connection: HubConnection | null = null;
  private connectionReady: Promise<void> | null = null;
  private variableUpdateCallbacks: VariableUpdateCallback[] = [];
  private alarmUpdateCallbacks: AlarmUpdateCallback[] = [];
  private groupUpdateCallbacks: GroupUpdateCallback[] = [];
  private plantStatusUpdateCallbacks: PlantStatusUpdateCallback[] = [];
  private statisticsUpdateCallbacks: StatisticsUpdateCallback[] = [];
  private gatewayDiagnosticsCallbacks: GatewayDiagnosticsCallback[] = [];
  private gatewayDiagnosticEventCallbacks: GatewayDiagnosticEventCallback[] = [];
  private gatewayLifecycleEventCallbacks: GatewayLifecycleEventCallback[] = [];
  private gatewayStartupDiagnosticsCallbacks: GatewayStartupDiagnosticsCallback[] = [];
  private gatewayFirmwareUpdateCallbacks: GatewayFirmwareUpdateCallback[] = [];
  private gatewayLogEntriesCallbacks: GatewayLogEntriesCallback[] = [];
  private maintDeadlineChangedCallbacks: MaintDeadlineChangedCallback[] = [];
  private maintInterventionOverdueCallbacks: MaintInterventionOverdueCallback[] = [];
  private pendingHandlers: Array<{ event: string; callback: (...args: any[]) => void }> = [];
  private subscribedPlants: Set<string> = new Set();
  private currentVariableSubscription: {
    plantId: number;
    variableNames: string[];
    pageContext: string;
  } | null = null;

  // ─── Connection status / health monitoring ────────────────────────────
  private status: ConnectionStatus = 'disconnected';
  private statusChangedAt: number = Date.now();
  private statusListeners: Set<ConnectionStatusListener> = new Set();
  private lastSuccessfulPingAt: number | null = null;
  private lastError: string | undefined = undefined;
  private restartAttempt = 0;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private pingFailures = 0;
  private restartTimer: ReturnType<typeof setTimeout> | null = null;
  /** Quando è stato esplicitamente richiesto stop (logout) — non tentare restart. */
  private stoppedExplicitly = false;

  async startConnection(): Promise<void> {
    this.stoppedExplicitly = false;
    if (this.connection?.state === HubConnectionState.Connected) {
      return;
    }
    await this.buildAndStart();
  }

  /** Costruisce una nuova connection, registra gli handler, avvia + ping loop. */
  private async buildAndStart(): Promise<void> {
    // Cleanup eventuale connection precedente prima di ricrearla.
    if (this.connection) {
      try { await this.connection.stop(); } catch { /* ignore */ }
      this.connection = null;
    }
    if (this.restartTimer) { clearTimeout(this.restartTimer); this.restartTimer = null; }

    this.setStatus('connecting');

    const token = localStorage.getItem('token');

    this.connection = new HubConnectionBuilder()
      .withUrl(HUB_URL, {
        accessTokenFactory: () => localStorage.getItem('token') || token || '',
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          // Exponential backoff: 0s, 2s, 4s, 8s, 16s, poi cap a 30s.
          // Quando si arrende → onclose → schedula restart via scheduleRestart()
          // (backoff infinito).
          const delay = Math.min(
            Math.pow(2, retryContext.previousRetryCount) * 1000,
            30000
          );
          return delay;
        },
      })
      // serverTimeoutInMilliseconds = quanto aspettare prima di considerare il
      // server "morto" se non riceviamo nulla. Default 30s — alziamo a 60s per
      // tolleranza maggiore ai jitter di rete (il ping app-level a 10s ci dà
      // comunque feedback molto prima).
      .withServerTimeout(60_000)
      // keepAliveInterval esplicito: 15s (default 15s, lo fissiamo per chiarezza).
      .withKeepAliveInterval(15_000)
      .configureLogging(LogLevel.Information)
      .build();

    this.registerHandlers();

    // Register any handlers that were added before connection was created
    this.pendingHandlers.forEach(({ event, callback }) => {
      this.connection!.on(event, callback);
    });
    // NOTA: NON svuotiamo pendingHandlers — devono restare per registrare anche
    // sulle connection ricreate dopo restart.

    this.connection.onreconnecting((error) => {
      console.warn('[SignalR] Riconnessione (auto)...', error);
      this.setStatus('reconnecting', error?.message);
    });

    this.connection.onreconnected(async (connectionId) => {
      console.info('[SignalR] Riconnesso. Connection ID:', connectionId);
      this.restartAttempt = 0;
      this.pingFailures = 0;
      this.setStatus('connected');
      await this.resubscribeAll();
    });

    this.connection.onclose((error) => {
      console.error('[SignalR] Connessione chiusa.', error);
      this.setStatus('disconnected', error?.message);
      this.stopPingLoop();
      // Se non è stato uno stop esplicito (logout) → schedule custom restart con
      // backoff infinito. Questo è il caso che oggi blocca l'app: il client
      // resta sicuro che è online perché niente segnala l'avvenuto fallimento.
      if (!this.stoppedExplicitly) {
        this.scheduleRestart();
      }
    });

    this.connectionReady = (async () => {
      try {
        await this.connection!.start();
        console.info('[SignalR] Connesso.');
        this.restartAttempt = 0;
        this.pingFailures = 0;
        this.lastSuccessfulPingAt = Date.now();
        this.setStatus('connected');
        this.startPingLoop();
        await this.resubscribeAll();
      } catch (err: any) {
        console.error('[SignalR] start() fallito:', err);
        this.setStatus('disconnected', err?.message ?? String(err));
        if (!this.stoppedExplicitly) this.scheduleRestart();
        throw err;
      }
    })();

    try { await this.connectionReady; } catch { /* swallow — restart già schedulato */ }
  }

  async stopConnection(): Promise<void> {
    this.stoppedExplicitly = true;
    this.stopPingLoop();
    if (this.restartTimer) { clearTimeout(this.restartTimer); this.restartTimer = null; }
    this.restartAttempt = 0;
    const conn = this.connection;
    this.connection = null;
    this.connectionReady = null;
    if (conn) {
      try { await conn.stop(); } catch { /* ignore */ }
      console.info('[SignalR] Disconnesso (esplicito).');
    }
    this.setStatus('disconnected');
  }

  /**
   * Forza un riavvio immediato della connection (bottone "Riprova ora" del banner).
   * Bypassa il backoff schedulato.
   */
  async forceReconnect(): Promise<void> {
    if (this.stoppedExplicitly) {
      console.warn('[SignalR] forceReconnect ignorato: stop esplicito attivo (utente non autenticato).');
      return;
    }
    if (this.restartTimer) { clearTimeout(this.restartTimer); this.restartTimer = null; }
    console.info('[SignalR] Force reconnect richiesto dall\'utente.');
    this.restartAttempt = 0;
    try { await this.buildAndStart(); } catch { /* status già aggiornato in buildAndStart */ }
  }

  // ─── Status / health monitoring ─────────────────────────────────────

  /** Stato corrente della connessione. */
  getConnectionStatus(): ConnectionStatus { return this.status; }

  /** Snapshot completo dello stato (per ConnectionStatusContext). */
  getConnectionInfo(): ConnectionStatusInfo {
    return {
      changedAt: this.statusChangedAt,
      lastSuccessfulPingAt: this.lastSuccessfulPingAt,
      restartAttempt: this.restartAttempt,
      lastError: this.lastError,
    };
  }

  /** Iscrive a notifiche di cambio stato. Restituisce funzione di unsubscribe. */
  onConnectionStatusChange(listener: ConnectionStatusListener): () => void {
    this.statusListeners.add(listener);
    // Emette subito lo stato corrente.
    try { listener(this.status, this.getConnectionInfo()); } catch { /* ignore */ }
    return () => { this.statusListeners.delete(listener); };
  }

  private setStatus(status: ConnectionStatus, error?: string): void {
    if (this.status === status && (!error || error === this.lastError)) return;
    this.status = status;
    this.statusChangedAt = Date.now();
    this.lastError = error;
    const info = this.getConnectionInfo();
    this.statusListeners.forEach((l) => {
      try { l(status, info); } catch (e) { console.error('[SignalR] status listener error', e); }
    });
  }

  // ─── Ping loop (heartbeat applicativo) ──────────────────────────────

  private startPingLoop(): void {
    this.stopPingLoop();
    this.pingTimer = setInterval(() => { this.doPing(); }, PING_INTERVAL_MS);
  }

  private stopPingLoop(): void {
    if (this.pingTimer) { clearInterval(this.pingTimer); this.pingTimer = null; }
    this.pingFailures = 0;
  }

  private async doPing(): Promise<void> {
    const conn = this.connection;
    if (!conn || conn.state !== HubConnectionState.Connected) {
      // Non abbiamo connessione completa: non contiamo come ping fallito (lo stato è già reconnecting/disconnected).
      return;
    }
    try {
      // Race tra invoke('Ping') e timeout. Se il server è "muto" SignalR non
      // ce ne accorgerebbe per ~60s (server timeout); con 5s qui siamo molto
      // più reattivi.
      const pingPromise = conn.invoke<string>('Ping');
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('ping timeout')), PING_TIMEOUT_MS),
      );
      await Promise.race([pingPromise, timeoutPromise]);
      this.lastSuccessfulPingAt = Date.now();
      this.pingFailures = 0;
      // Se stavamo in reconnecting e il ping passa, siamo tornati operativi:
      // SignalR aggiorna lo stato comunque, ma forziamo qui per coerenza.
      if (this.status !== 'connected') this.setStatus('connected');
    } catch (err: any) {
      this.pingFailures++;
      console.warn(`[SignalR] Ping fallito (${this.pingFailures}/${PING_FAILURES_BEFORE_RESET}):`, err?.message ?? err);
      if (this.pingFailures >= PING_FAILURES_BEFORE_RESET) {
        // Il WebSocket pensa di essere connesso ma il server non risponde →
        // forziamo restart. Questo è proprio il caso "freeze silenzioso".
        console.error('[SignalR] Connessione freezzata, forzo restart.');
        this.pingFailures = 0;
        this.setStatus('reconnecting', 'Connessione non risponde, riavvio...');
        // buildAndStart farà stop+start sulla connection — onclose schedulerà restart con backoff.
        try { await conn.stop(); } catch { /* ignore */ }
        // onclose handler farà partire scheduleRestart automaticamente.
      }
    }
  }

  // ─── Custom restart loop con backoff infinito ───────────────────────

  private scheduleRestart(): void {
    if (this.stoppedExplicitly) return;
    if (this.restartTimer) return; // già schedulato
    const idx = Math.min(this.restartAttempt, RESTART_BACKOFF_MS.length - 1);
    const delay = RESTART_BACKOFF_MS[idx];
    this.restartAttempt++;
    console.info(`[SignalR] Restart schedulato fra ${delay}ms (tentativo ${this.restartAttempt})`);
    this.setStatus('reconnecting', this.lastError);
    this.restartTimer = setTimeout(() => {
      this.restartTimer = null;
      if (this.stoppedExplicitly) return;
      this.buildAndStart().catch(() => {
        // buildAndStart già schedula il prossimo restart via onclose.
      });
    }, delay);
  }

  /** Re-subscribe a tutti i plant + variabili. Idempotente. */
  private async resubscribeAll(): Promise<void> {
    if (!this.connection || this.connection.state !== HubConnectionState.Connected) return;
    for (const plantId of Array.from(this.subscribedPlants)) {
      try {
        await this.connection.invoke('SubscribeToPlant', plantId);
        console.info('[SignalR] Re-subscribed to plant', plantId);
      } catch (err) {
        console.error('[SignalR] Failed to re-subscribe to plant', plantId, err);
      }
    }
    if (this.currentVariableSubscription) {
      const { plantId, variableNames, pageContext } = this.currentVariableSubscription;
      try {
        await this.connection.invoke('SubscribeVariables', plantId, variableNames, pageContext);
        console.info(`[SignalR] Re-subscribed to ${variableNames.length} variables (${pageContext}) for plant ${plantId}`);
      } catch (err) {
        console.error('[SignalR] Failed to re-subscribe to variables:', err);
      }
    }
  }

  async subscribeToPlant(plantId: string): Promise<void> {
    this.subscribedPlants.add(plantId);
    await this.waitForConnection();
    if (this.connection?.state === HubConnectionState.Connected) {
      await this.connection.invoke('SubscribeToPlant', plantId);
    }
  }

  async unsubscribeFromPlant(plantId: string): Promise<void> {
    this.subscribedPlants.delete(plantId);
    if (this.connection?.state === HubConnectionState.Connected) {
      await this.connection.invoke('UnsubscribeFromPlant', plantId);
    }
  }

  private async waitForConnection(): Promise<void> {
    if (this.connection?.state === HubConnectionState.Connected) return;
    if (this.connectionReady) {
      try { await this.connectionReady; } catch { /* ignore */ }
    }
  }

  /**
   * Subscribe to specific variables for a plant.
   * The server will only send updates for these variables.
   */
  async subscribeVariables(plantId: number, variableNames: string[], pageContext: string): Promise<void> {
    this.currentVariableSubscription = { plantId, variableNames, pageContext };
    try {
      await this.waitForConnection();
      if (this.connection?.state === HubConnectionState.Connected) {
        await this.connection.invoke('SubscribeVariables', plantId, variableNames, pageContext);
        console.log(`[SignalR] Subscribed to ${variableNames.length} variables (${pageContext}) for plant ${plantId}`);
      }
    } catch (error) {
      console.error('[SignalR] Error subscribing to variables:', error);
    }
  }

  /**
   * Unsubscribe from all variables.
   */
  async unsubscribeVariables(): Promise<void> {
    this.currentVariableSubscription = null;
    try {
      if (this.connection?.state === HubConnectionState.Connected) {
        await this.connection.invoke('UnsubscribeVariables');
        console.log('[SignalR] Unsubscribed from all variables');
      }
    } catch (error) {
      console.error('[SignalR] Error unsubscribing from variables:', error);
    }
  }

  // --- Event subscriptions ---

  onVariableUpdate(callback: VariableUpdateCallback): () => void {
    this.variableUpdateCallbacks.push(callback);
    return () => {
      this.variableUpdateCallbacks = this.variableUpdateCallbacks.filter(
        (cb) => cb !== callback
      );
    };
  }

  onAlarmUpdate(callback: AlarmUpdateCallback): () => void {
    this.alarmUpdateCallbacks.push(callback);
    return () => {
      this.alarmUpdateCallbacks = this.alarmUpdateCallbacks.filter(
        (cb) => cb !== callback
      );
    };
  }

  onGroupUpdate(callback: GroupUpdateCallback): () => void {
    this.groupUpdateCallbacks.push(callback);
    return () => {
      this.groupUpdateCallbacks = this.groupUpdateCallbacks.filter(
        (cb) => cb !== callback
      );
    };
  }

  onPlantStatusUpdate(callback: PlantStatusUpdateCallback): () => void {
    this.plantStatusUpdateCallbacks.push(callback);
    return () => {
      this.plantStatusUpdateCallbacks = this.plantStatusUpdateCallbacks.filter(
        (cb) => cb !== callback
      );
    };
  }

  onStatisticsUpdate(callback: StatisticsUpdateCallback): () => void {
    this.statisticsUpdateCallbacks.push(callback);
    return () => {
      this.statisticsUpdateCallbacks = this.statisticsUpdateCallbacks.filter(
        (cb) => cb !== callback
      );
    };
  }

  // --- Gateway v2.0 push events ---

  onGatewayDiagnostics(callback: GatewayDiagnosticsCallback): () => void {
    this.gatewayDiagnosticsCallbacks.push(callback);
    return () => { this.gatewayDiagnosticsCallbacks = this.gatewayDiagnosticsCallbacks.filter(cb => cb !== callback); };
  }

  onGatewayDiagnosticEvent(callback: GatewayDiagnosticEventCallback): () => void {
    this.gatewayDiagnosticEventCallbacks.push(callback);
    return () => { this.gatewayDiagnosticEventCallbacks = this.gatewayDiagnosticEventCallbacks.filter(cb => cb !== callback); };
  }

  onGatewayLifecycleEvent(callback: GatewayLifecycleEventCallback): () => void {
    this.gatewayLifecycleEventCallbacks.push(callback);
    return () => { this.gatewayLifecycleEventCallbacks = this.gatewayLifecycleEventCallbacks.filter(cb => cb !== callback); };
  }

  onGatewayStartupDiagnostics(callback: GatewayStartupDiagnosticsCallback): () => void {
    this.gatewayStartupDiagnosticsCallbacks.push(callback);
    return () => { this.gatewayStartupDiagnosticsCallbacks = this.gatewayStartupDiagnosticsCallbacks.filter(cb => cb !== callback); };
  }

  onGatewayFirmwareUpdate(callback: GatewayFirmwareUpdateCallback): () => void {
    this.gatewayFirmwareUpdateCallbacks.push(callback);
    return () => { this.gatewayFirmwareUpdateCallbacks = this.gatewayFirmwareUpdateCallbacks.filter(cb => cb !== callback); };
  }

  onGatewayLogEntries(callback: GatewayLogEntriesCallback): () => void {
    this.gatewayLogEntriesCallbacks.push(callback);
    return () => { this.gatewayLogEntriesCallbacks = this.gatewayLogEntriesCallbacks.filter(cb => cb !== callback); };
  }

  // --- Maintenance events ---

  onMaintenanceDeadlineChanged(callback: MaintDeadlineChangedCallback): () => void {
    this.maintDeadlineChangedCallbacks.push(callback);
    return () => { this.maintDeadlineChangedCallbacks = this.maintDeadlineChangedCallbacks.filter(cb => cb !== callback); };
  }

  onMaintenanceInterventionOverdue(callback: MaintInterventionOverdueCallback): () => void {
    this.maintInterventionOverdueCallbacks.push(callback);
    return () => { this.maintInterventionOverdueCallbacks = this.maintInterventionOverdueCallbacks.filter(cb => cb !== callback); };
  }

  // --- Generic event methods (used by pages) ---

  on(eventName: string, callback: (...args: any[]) => void): void {
    if (this.connection) {
      this.connection.on(eventName, callback);
    } else {
      // Queue handler to be registered when connection is created
      this.pendingHandlers.push({ event: eventName, callback });
    }
  }

  off(eventName: string, callback: (...args: any[]) => void): void {
    if (this.connection) {
      this.connection.off(eventName, callback);
    }
    // Also remove from pending if queued
    this.pendingHandlers = this.pendingHandlers.filter(
      h => !(h.event === eventName && h.callback === callback)
    );
  }

  async invoke(methodName: string, ...args: any[]): Promise<void> {
    await this.waitForConnection();
    if (this.connection?.state === HubConnectionState.Connected) {
      await this.connection.invoke(methodName, ...args);
    }
  }

  // --- Internal ---

  private registerHandlers(): void {
    if (!this.connection) return;

    // No-op fallback per eventi che il backend invia a Clients.All / gruppi
    // plant ma che la pagina corrente non sempre osserva. Senza fallback la lib
    // SignalR sputa "Warning: No client method with the name 'plantupdated' found"
    // a ogni evento (centinaia/min su sistemi attivi). I consumer dinamici
    // (Layout, PlantDetail) registrano i propri handler IN AGGIUNTA — SignalR
    // supporta più handler per stesso event name e li chiama tutti in catena.
    this.connection.on('PlantUpdated', () => { /* gestito dinamicamente da Layout */ });
    this.connection.on('GroupDataUpdated', () => { /* gestito dinamicamente da PlantDetail */ });

    // Backend sends: VariablesUpdated(plantId, VariableUpdateDto[])
    // where VariableUpdateDto = { plantId, variableName, value, timestamp }
    this.connection.on('VariablesUpdated', (plantId: number, updates: any[]) => {
      if (!updates || !Array.isArray(updates)) return;
      for (const u of updates) {
        // Map VariableUpdateDto to a pseudo-Variable with name as key
        const pseudoVariable = {
          id: 0,
          name: u.variableName,
          value: u.value,
        } as Variable;
        this.variableUpdateCallbacks.forEach((cb) => cb(pseudoVariable));
      }
    });

    this.connection.on('AlarmUpdated', (alarm: Alarm) => {
      this.alarmUpdateCallbacks.forEach((cb) => cb(alarm));
    });

    this.connection.on('GroupUpdated', (group: Group) => {
      this.groupUpdateCallbacks.forEach((cb) => cb(group));
    });

    this.connection.on(
      'PlantStatusUpdated',
      (plantId: string, status: PlantStatus) => {
        this.plantStatusUpdateCallbacks.forEach((cb) => cb(plantId, status));
      }
    );

    this.connection.on('StatisticsUpdated', (plantId: number) => {
      this.statisticsUpdateCallbacks.forEach((cb) => cb(plantId));
    });

    // --- Gateway v2.0: push events dal GatewayHub ---

    this.connection.on('GatewayDiagnostics', (gatewayId: string, report: DiagnosticReport) => {
      this.gatewayDiagnosticsCallbacks.forEach(cb => cb(gatewayId, report));
    });

    this.connection.on('GatewayDiagnosticEvent', (gatewayId: string, evt: DiagnosticEvent) => {
      this.gatewayDiagnosticEventCallbacks.forEach(cb => cb(gatewayId, evt));
    });

    this.connection.on('GatewayLifecycleEvent', (gatewayId: string, evt: LifecycleEvent) => {
      this.gatewayLifecycleEventCallbacks.forEach(cb => cb(gatewayId, evt));
    });

    this.connection.on('GatewayStartupDiagnostics', (gatewayId: string, diag: StartupDiagnostics) => {
      this.gatewayStartupDiagnosticsCallbacks.forEach(cb => cb(gatewayId, diag));
    });

    this.connection.on('GatewayFirmwareUpdate', (gatewayId: string, status: FirmwareUpdateStatus) => {
      this.gatewayFirmwareUpdateCallbacks.forEach(cb => cb(gatewayId, status));
    });

    this.connection.on('GatewayLogEntries', (gatewayId: string, entries: any[]) => {
      this.gatewayLogEntriesCallbacks.forEach(cb => cb(gatewayId, entries));
    });

    // --- Maintenance push events ---

    this.connection.on('MaintenanceDeadlineChanged', (evt: MaintDeadlineChangedEvent) => {
      this.maintDeadlineChangedCallbacks.forEach(cb => cb(evt));
    });

    this.connection.on('MaintenanceInterventionOverdue', (evt: MaintInterventionOverdueEvent) => {
      this.maintInterventionOverdueCallbacks.forEach(cb => cb(evt));
    });
  }
}

const signalRService = new SignalRService();
export default signalRService;
